import React from 'react';

function formatDisplayTime(val) {
  if (!val) return '8:00 AM';
  const [hh, mm] = val.split(':');
  const h = parseInt(hh, 10);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const display = h % 12 === 0 ? 12 : h % 12;
  return `${display}:${mm} ${suffix}`;
}

const SUCCESS_CSS = `
  @keyframes dropletBounce {
    0%, 100% { transform: translateY(0) scale(1); }
    40% { transform: translateY(-18px) scale(1.1); }
    60% { transform: translateY(-8px) scale(1.05); }
  }
  @keyframes successFadeUp {
    from { opacity: 0; transform: translateY(24px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .droplet-icon { animation: dropletBounce 2.4s ease-in-out infinite; }
  .success-card { animation: successFadeUp 0.6s ease both; }
  .success-row:hover { background: rgba(14,165,233,0.08) !important; }
  .success-row { transition: background 0.15s; }
`;

export default function SuccessPage({ data = {}, onManage }) {
  const { email, phone, name, deliveryTime, plan } = data;
  const links = plan === 'pro' ? 10 : 5;
  const planLabel = plan === 'pro' ? 'Pro' : 'Basic';

  return (
    <>
      <style>{SUCCESS_CSS}</style>
      <div style={s.page}>
        {/* BG orbs */}
        <div style={s.orb1} />
        <div style={s.orb2} />

        <div className="success-card" style={s.card}>
          {/* Icon */}
          <div className="droplet-icon" style={s.iconWrap}>💧</div>

          {/* Headline */}
          <h1 style={s.title}>
            You're in{name ? `, ${name}` : ''}!
          </h1>
          <p style={s.subtitle}>
            Your daily digest is all set. Check your inbox for a welcome message —
            your first real droplet arrives at the time you chose.
          </p>

          {/* Summary */}
          <div style={s.summaryBox}>
            <div className="success-row" style={s.row}>
              <span style={s.rowIcon}>{email ? '📧' : '📱'}</span>
              <div>
                <div style={s.rowLabel}>Delivering to</div>
                <div style={s.rowVal}>{email || phone || 'Your inbox'}</div>
              </div>
            </div>
            <div style={s.divider} />
            <div className="success-row" style={s.row}>
              <span style={s.rowIcon}>⏰</span>
              <div>
                <div style={s.rowLabel}>Delivery time</div>
                <div style={s.rowVal}>Every day at {formatDisplayTime(deliveryTime)}</div>
              </div>
            </div>
            <div style={s.divider} />
            <div className="success-row" style={s.row}>
              <span style={s.rowIcon}>💧</span>
              <div>
                <div style={s.rowLabel}>Your digest</div>
                <div style={s.rowVal}>{links} personalized links, daily{plan ? ` · ${planLabel} plan` : ''}</div>
              </div>
            </div>
          </div>

          {/* Tips */}
          <div style={s.tipBox}>
            <div style={s.tipTitle}>💡 Pro tips</div>
            <ul style={s.tipList}>
              <li style={s.tipItem}>Check your spam folder if you don't see the welcome message</li>
              <li style={s.tipItem}>Manage or cancel anytime from your account settings</li>
              <li style={s.tipItem}>Links open directly in your browser — no login needed</li>
            </ul>
          </div>

          {/* Manage link */}
          {onManage && (
            <button onClick={onManage} style={s.manageBtn}>
              ⚙️ Manage topics, time & subscription
            </button>
          )}

          <p style={s.footer}>© {new Date().getFullYear()} DailyDroplet</p>
        </div>
      </div>
    </>
  );
}

const s = {
  page: {
    minHeight: '100vh',
    background: '#060d1f',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '24px 16px',
    fontFamily: "'Inter', system-ui, sans-serif",
    position: 'relative', overflow: 'hidden',
  },
  orb1: {
    position: 'absolute', top: '10%', left: '20%',
    width: 400, height: 400, borderRadius: '50%',
    background: 'radial-gradient(circle,rgba(14,165,233,0.12) 0%,transparent 70%)',
    filter: 'blur(20px)', pointerEvents: 'none',
  },
  orb2: {
    position: 'absolute', bottom: '10%', right: '15%',
    width: 300, height: 300, borderRadius: '50%',
    background: 'radial-gradient(circle,rgba(99,102,241,0.10) 0%,transparent 70%)',
    filter: 'blur(20px)', pointerEvents: 'none',
  },
  card: {
    background: 'rgba(255,255,255,0.03)',
    border: '1px solid #1e293b',
    borderRadius: 24,
    padding: 'clamp(32px,6vw,56px)',
    maxWidth: 480, width: '100%',
    textAlign: 'center',
    position: 'relative', zIndex: 1,
    boxShadow: '0 40px 80px rgba(0,0,0,0.4)',
  },
  iconWrap: { fontSize: 64, marginBottom: 20, display: 'block' },
  title: { fontSize: 36, fontWeight: 800, color: '#f8fafc', marginBottom: 14 },
  subtitle: { fontSize: 16, color: '#64748b', lineHeight: 1.7, marginBottom: 32 },
  summaryBox: {
    background: '#0a1628', border: '1px solid #1e3a5f',
    borderRadius: 16, overflow: 'hidden', marginBottom: 24, textAlign: 'left',
  },
  row: { display: 'flex', alignItems: 'center', gap: 16, padding: '16px 20px' },
  rowIcon: { fontSize: 22, flexShrink: 0 },
  rowLabel: { fontSize: 11, color: '#334155', fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 3 },
  rowVal: { fontSize: 15, color: '#94a3b8', fontWeight: 500 },
  divider: { height: 1, background: '#1e3a5f', margin: '0 20px' },
  tipBox: {
    background: 'rgba(14,165,233,0.05)', border: '1px solid rgba(14,165,233,0.15)',
    borderRadius: 12, padding: '16px 20px', textAlign: 'left', marginBottom: 28,
  },
  tipTitle: { fontSize: 13, fontWeight: 700, color: '#38bdf8', marginBottom: 10 },
  tipList: { listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 },
  tipItem: { fontSize: 13, color: '#64748b', paddingLeft: 16, position: 'relative', lineHeight: 1.5 },
  manageBtn: {
    display: 'block', width: '100%', padding: '13px',
    background: 'none', border: '1px solid #1e3a5f',
    color: '#64748b', borderRadius: 12, cursor: 'pointer',
    fontFamily: "'Inter', system-ui, sans-serif",
    fontSize: 14, fontWeight: 600, marginBottom: 16,
    transition: 'all 0.18s',
  },
  footer: { fontSize: 12, color: '#1e3a5f', marginTop: 8 },
};
